import { useState } from 'react';
import { decodeOffer, linkVerificationCode, type VavClient } from '@vav/client-core';
import { useT } from '../i18n.tsx';

/**
 * The landing point of a scanned linking QR.
 *
 * The QR on the new device is an https link into this app; the native camera
 * opens it, the boot code finds `?approve=…` and mounts this. Everything the
 * user must do is one comparison and one tap: the six digits here are derived
 * from the scanned code, the new device shows its own six, and approving on a
 * mismatch is the only wrong move.
 *
 * The account named in the dialog is the active profile — with several
 * profiles, whichever one is open is the one the new device joins.
 */
export function ApproveLinkDialog({
  client,
  code,
  onClose,
}: {
  client: VavClient;
  code: string;
  onClose: () => void;
}) {
  const t = useT();
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle');

  const digits = (() => {
    try {
      return linkVerificationCode(decodeOffer(code).ephemeralPub);
    } catch {
      return undefined;
    }
  })();

  async function approve() {
    setState('busy');
    try {
      await client.approveLinking(code);
      setState('done');
    } catch {
      setState('failed');
    }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('link.approve.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__header">
          <h2>{t('link.approve.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('profile.name.cancel')}>
            ✕
          </button>
        </header>

        <p className="hint">
          {t('link.approve.account', {
            name: client.account?.displayName ?? client.account?.username ?? '',
          })}
        </p>

        {digits ? (
          <>
            <p className="link__verify" dir="ltr">
              {digits}
            </p>
            <p className="hint">{t('profile.link.verifyHint')}</p>
          </>
        ) : (
          <p className="error">{t('profile.link.failed')}</p>
        )}

        {state === 'failed' && <p className="error">{t('profile.link.failed')}</p>}
        {state === 'done' ? (
          <>
            <p className="muted">✓ {t('profile.link.done')}</p>
            <button className="primary" onClick={onClose}>
              {t('profile.name.save')}
            </button>
          </>
        ) : (
          <button
            className="primary"
            disabled={!digits || state === 'busy'}
            onClick={() => void approve()}
          >
            {t('profile.link.approve')}
          </button>
        )}
      </div>
    </div>
  );
}
